import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Equipment } from '../../types/equipment';
import { updateEquipment } from '../../features/equipment/equipmentSlice';
import { useFormValidation } from '../../hooks/useFormValidation';
import Card from '../ui/Card';
import Button from '../ui/Button';

interface Props {
  equipment: Equipment;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditEquipmentForm: React.FC<Props> = ({ equipment, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { values, errors, handleChange, handleSubmit, isValid } = useFormValidation(
    {
      name: equipment.name,
      type: equipment.type,
      purchase_date: equipment.purchase_date || '',
      maintenance_due: equipment.maintenance_due || '',
      status: equipment.status,
      notes: equipment.notes || '',
    },
    {
      name: (value) => (!value ? t('equipment.validation.nameRequired') : ''),
      type: (value) => (!value ? t('equipment.validation.typeRequired') : ''),
      maintenance_due: (value, allValues) => {
        if (value && allValues.purchase_date && new Date(value) < new Date(allValues.purchase_date)) {
          return t('equipment.validation.maintenanceDateError');
        }
        return '';
      },
    },
    async (values) => {
      try {
        await dispatch(updateEquipment({ id: equipment.id, ...values }));
        onSuccess();
      } catch (error) {
        console.error('Error updating equipment:', error);
      }
    }
  );

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              {t('equipment.equipmentName')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              aria-required="true"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              {t('equipment.type')} *
            </label>
            <input
              type="text"
              id="type"
              name="type"
              value={values.type}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              aria-required="true"
            />
            {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type}</p>}
          </div>

          <div>
            <label htmlFor="purchase_date" className="block text-sm font-medium text-gray-700">
              {t('equipment.purchaseDate')}
            </label>
            <input
              type="date"
              id="purchase_date"
              name="purchase_date"
              value={values.purchase_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="maintenance_due" className="block text-sm font-medium text-gray-700">
              {t('equipment.maintenanceDue')}
            </label>
            <input
              type="date"
              id="maintenance_due"
              name="maintenance_due"
              value={values.maintenance_due}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            />
            {errors.maintenance_due && (
              <p className="mt-1 text-sm text-red-600">{errors.maintenance_due}</p>
            )}
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              {t('common.status')}
            </label>
            <select
              id="status"
              name="status"
              value={values.status}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            >
              <option value="active">{t('equipment.status.active')}</option>
              <option value="maintenance">{t('equipment.status.maintenance')}</option>
              <option value="retired">{t('equipment.status.retired')}</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            {t('equipment.equipmentNotes')}
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            value={values.notes}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default EditEquipmentForm;